import Papa from 'papaparse';
import ModifiedProduct from '../model/ModifiedProduct';
import Category from '../model/Quartiles';
import Quartiles from '../model/Quartiles';

export function formatPrice(value: any): string {
  const formattedPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(value);
  if (formattedPrice === '€NaN') {
    return value;
  }
  return formattedPrice;
}

export function capitalizeFirstLetter(input: string): string {
  return input.charAt(0).toUpperCase() + input.slice(1);
}

export function ecsN(
  ecs: number,
  quartiles: Quartiles,
  isGlobal: boolean,
  category: string,
): number {
  let correctQuartiles: number[];

  if (isGlobal) {

    correctQuartiles = quartiles.global_quartiles;
  } else {
    correctQuartiles = quartiles.sub_category_quartiles[category];
  }

  if (correctQuartiles === undefined || correctQuartiles.length < 6) {
    return -1;
  }
  if (ecs > correctQuartiles[5]) {
    return 5;
  } else if (ecs > correctQuartiles[4]) {
    return 4;
  } else if (ecs > correctQuartiles[3]) {
    return 3;
  } else if (ecs > correctQuartiles[2]) {
    return 2;
  } else if (ecs > correctQuartiles[1]) {
    return 1;
  } else if (ecs >= correctQuartiles[0]) {
    return 0;
  } else {
    return -1;
  }
}

export function ecsColor(
  ecs: number,
  quartiles: Quartiles,
  isGlobal: boolean,
  category: string,
) {
  let correctQuartiles: number[];
  if (isGlobal) {
    correctQuartiles = quartiles.global_quartiles;
  } else {
    correctQuartiles = quartiles.sub_category_quartiles[category];
  }

  if (
    correctQuartiles === undefined ||
    correctQuartiles === null ||
    correctQuartiles.length < 6
  ) {
    return 'bg-gray';
  }
  if (ecs > correctQuartiles[5]) {
    return 'bg-score6';
  } else if (ecs > correctQuartiles[4]) {
    return 'bg-score5';
  } else if (ecs > correctQuartiles[3]) {
    return 'bg-score4';
  } else if (ecs > correctQuartiles[2]) {
    return 'bg-score3';
  } else if (ecs > correctQuartiles[1]) {
    return 'bg-score2';
  } else if (ecs >= correctQuartiles[0]) {
    return 'bg-score1';
  } else {
    return 'bg-gray';
  }
}

export function ecsLetter(
  ecs: number,
  quartiles: Category,
  isGlobal: boolean,
  category: string,
) {
  let correctQuartiles: number[];
  if (isGlobal) {
    correctQuartiles = quartiles.global_quartiles;
  } else {
    correctQuartiles = quartiles.sub_category_quartiles[category!!];
  }

  if (
    correctQuartiles === undefined ||
    correctQuartiles === null ||
    correctQuartiles.length < 6
  ) {
    return '?';
  }
  if (ecs > correctQuartiles[5]) {
    return 'F';
  } else if (ecs > correctQuartiles[4]) {
    return 'E';
  } else if (ecs > correctQuartiles[3]) {
    return 'D';
  } else if (ecs > correctQuartiles[2]) {
    return 'C';
  } else if (ecs > correctQuartiles[1]) {
    return 'B';
  } else if (ecs >= correctQuartiles[0]) {
    return 'A';
  } else {
    return '?';
  }
}

export function reliabilityColor(reliability: number) {
  if (reliability > 83.5) {
    return 'bg-score1';
  } else if (reliability > 67) {
    return 'bg-score2';
  } else if (reliability > 50.5) {
    return 'bg-score3';
  } else if (reliability > 34) {
    return 'bg-score4';
  } else if (reliability > 17.5) {
    return 'bg-score5';
  } else if (reliability > 0) {
    return 'bg-score6';
  } else {
    return 'bg-gray';
  }
}

export function getMaterialsFromName(name: string, fullMaterials: any) {
  try {
    return fullMaterials.find((e: any) => e.name === name).id;
  } catch (e) {
    console.error(e);
  }
  return '';
}

export function getMaterialsFromId(id: string, fullMaterials: any) {
  try {
    return fullMaterials.find((e: any) => e.id === id).name;
  } catch (e) {
    console.error(e);
  }
  return '';
}

export function getCountryFromName(name: string, fullCountries: any) {
  try {
    return fullCountries.find((e: any) => e.name === name).code;
  } catch (e) {
    console.error(e);
  }
  return '';
}

export function getCountryFromCode(code: string, fullCountries: any) {
  try {
    return fullCountries.find((e: any) => e.code === code).name;
  } catch (e) {
    console.error(e);
  }
  return '';
}

export function getImpactName(acronyme: string) {
  switch (acronyme) {
    case 'ecs':
      return 'Coût environnemental';
    case 'acd':
      return 'Acidification';
    case 'ozd':
      return "Appauvrissement de la couche d'ozone";
    case 'cch':
      return 'Changement climatique';
    case 'fwe':
      return 'Eutrophisation eaux douces';
    case 'swe':
      return 'Eutrophisation marine';
    case 'tre':
      return 'Eutrophisation terrestre';
    case 'pco':
      return "Formation d'ozone photochimique";
    case 'pma':
      return 'Particules';
    case 'ior':
      return 'Radiations ionisantes';
    case 'fru':
      return 'Utilisation de ressources fossiles';
    case 'mru':
      return 'Utilisation de ressources minérales et métalliques';
    case 'ldu':
      return 'Utilisation des sols';
    case 'bvi':
      return 'Biodiversité locale';
    case 'etf':
      return `Écotoxicité de l'eau douce`;
    case 'htc':
      return 'Toxicité humaine - cancer';
    case 'htn':
      return 'Toxicité humaine - non-cancer';
    // case 'pef':
    //   return 'Score PEF';
    case 'wtu':
      return 'Utilisation de ressources en eau';
    default:
      return '';
  }
}

export function getGlobalName(index: number) {
  switch (index) {
    case 0:
      return 'Matières premières';
    case 1:
      return 'Transformation';
    case 2:
      return 'Transports';
    case 3:
      return 'Utilisation';
    case 4:
      return 'Fin de vie';
    default:
      return '';
  }
}

export async function fetchCSVData() {
  const response = await fetch('./data/ordered-rg.csv');
  const text = await response.text();
  const results = Papa.parse(text, {
    header: true,
    dynamicTyping: true,
  });
  return results.data;
}

export async function fetchLocation() {
  try {
    if ('geolocation' in navigator) {
      const position: any = await getPosition();
      const latitude = position.coords.latitude;
      const longitude = position.coords.longitude;
      return [latitude, longitude];
    } else {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      // eslint-disable-next-line no-sequences
      return data.city, data.region;
    }
  } catch (e) {
    return null;
  }
}

function getPosition(options?: PositionOptions): Promise<any> {
  return new Promise((resolve, reject) =>
    navigator.geolocation.getCurrentPosition(resolve, reject, options),
  );
}

export function validateForm(
  formValues: ModifiedProduct,
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>,
) {
  let isValid = true;

  if (formValues.weight !== undefined && formValues.weight <= 0.01) {
    setErrorMessage('Le poids doit être supérieur à 0.01.');
    isValid = false;
  }

  if (
    formValues.transp_aer !== undefined &&
    (parseFloat(formValues.transp_aer) < 0 ||
      parseFloat(formValues.transp_aer) > 1 ||
      isNaN(parseFloat(formValues.transp_aer)))
  ) {
    setErrorMessage(
      'Le ratio de transport aérien doit être compris entre 0 et 1.',
    );
    isValid = false;
  }

  if (
    formValues.quality !== undefined &&
    (parseFloat(formValues.quality) < 0.67 ||
      parseFloat(formValues.quality) > 1.45 ||
      isNaN(parseFloat(formValues.quality)))
  ) {
    setErrorMessage('La qualité doit être comprise entre 0.67 et 1.45.');
    isValid = false;
  }

  if (
    formValues.reparability !== undefined &&
    (parseFloat(formValues.reparability) < 1 ||
      parseFloat(formValues.reparability) > 1.15 ||
      isNaN(parseFloat(formValues.reparability)))
  ) {
    setErrorMessage('La réparabilité doit être comprise entre 1 et 1.15.');
    isValid = false;
  }

  if (
    formValues.makingWaste !== undefined &&
    (parseFloat(formValues.makingWaste) < 0 ||
      parseFloat(formValues.makingWaste) > 0.4 ||
      isNaN(parseFloat(formValues.makingWaste)))
  ) {
    setErrorMessage(
      'Le taux de perte en confection doit être compris entre 0 et 0.4.',
    );
    isValid = false;
  }

  if (
    formValues.makingDeadStock !== undefined &&
    (parseFloat(formValues.makingDeadStock) < 0 ||
      parseFloat(formValues.makingDeadStock) > 0.3 ||
      isNaN(parseFloat(formValues.makingDeadStock)))
  ) {
    setErrorMessage(
      'Le taux de stocks dormant doit être compris entre 0 et 0.3.',
    );
    isValid = false;
  }

  if (
    formValues.yarnSize &&
    !/^(\d+(\.\d+)?)(\s*(Nm|Detex))?$/.test(formValues.yarnSize)
  ) {
    setErrorMessage(
      "Le titrage du fil doit être un nombre suivi éventuellement de 'Nm' ou 'Detex'.",
    );
    isValid = false;
  }

  if (
    formValues.surfaceMass !== undefined &&
    (parseFloat(formValues.surfaceMass) < 80 ||
      parseFloat(formValues.surfaceMass) > 500 ||
      isNaN(parseFloat(formValues.surfaceMass)))
  ) {
    setErrorMessage(
      "Le grammage de l'étoffe doit être compris entre 80 et 500.",
    );
    isValid = false;
  }

  return isValid;
}
